import { TaskStatus } from "@common/types/task";
import { TaskColumn } from "@common/types/views/tasksView";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import type { DataGridColumn } from "@design-system/DataGrid";
import {
    OneClickDoneHoverIcon,
    OneClickDoneIcon,
    OneClickTodoHoverIcon,
    OneClickTodoIcon,
} from "@icons/taskStatuses";
import type { NestedTaskRow } from "../types";
import { TaskGroupLabel } from "./cells/TaskGroupLabel";
import { hasTaskFields } from "./cells/taskRowCells";
import { TaskTitleExtras } from "./cells/TaskTitleExtras";
import { col, commitTask, type TaskColumnBuild } from "./editableTaskColumns";

const renderStatusIcon = ({
    value,
    isPreview,
}: {
    value: TaskStatus | undefined;
    isPreview: boolean;
}) => {
    if (value === TaskStatus.DONE) {
        return isPreview ? <OneClickDoneHoverIcon /> : <OneClickDoneIcon />;
    }
    return isPreview ? <OneClickTodoHoverIcon /> : <OneClickTodoIcon />;
};

const isDone = (row: NestedTaskRow) => row.status === TaskStatus.DONE;

/** The task's one-click Done / To do circle; finishing a blocked task asks first. */
const buildStatusToggle = (actions: TaskColumnBuild["actions"]) =>
    col.toggle({
        value: (row) => row.status,
        next: (row) => (isDone(row) ? TaskStatus.TODO : TaskStatus.DONE),
        commit: ({ row, value }) =>
            hasTaskFields(row) && value
                ? actions.changeStatus({ task: row, status: value })
                : undefined,
        label: (row) => (isDone(row) ? "Mark To do" : "Mark Done"),
        renderIcon: renderStatusIcon,
        isDisabled: (row) => isBusyClosesDemoId(row.id),
    });

const getTaskId = (row: NestedTaskRow) =>
    row.isNewPlaceholder ? undefined : row.id;

/**
 * The Task column: the tree's label, pinned first. Task rows show the status
 * toggle in the chevron's slot, the copyable title, then the blocked badge
 * and viewers; group rows show `TaskGroupLabel`; draft rows the grid's
 * "Create task" input. The title edits in place only when `isTitleEditable`.
 */
export const buildTitleColumn = ({
    options,
    actions,
    isTitleEditable,
}: TaskColumnBuild & {
    isTitleEditable: boolean;
}): DataGridColumn<NestedTaskRow> =>
    col.text(
        { id: TaskColumn.NAME, value: (row) => row.title },
        {
            ...options,
            tree: true,
            pin: "start",
            leading: buildStatusToggle(actions),
            leadingPlacement: "toggleSlot",
            trailing: TaskTitleExtras,
            copyId: getTaskId,
            copyLabel: "Task",
            summary: TaskGroupLabel,
            edit: isTitleEditable
                ? col.edit.text({
                      value: (row) => row.title ?? "",
                      commit: commitTask({
                          actions,
                          toBody: (title) => ({ title }),
                      }),
                      placeholder: "Add title",
                  })
                : undefined,
        },
    );
